/**
 * B12, resolved: the inspector shows scoring, and the RUBRIC owns it.
 *
 * `design/screen-manifest.md` B12: "The node inspector edits … Options with
 * per-option scores … Per-option scoring lives on the block here, but §3.2
 * keeps scoring in the rubric. Which owns it?"
 *
 * Story 6.3's criterion answers it: "The inspector surfaces the mapping inline
 * for convenience but writes to `rubric`, so §3.2 keeps one owner for scoring."
 *
 * So this module is the whole of that seam, and it exists so no component ever
 * has to know it. A component asks `pointsFor(funnel, blockId, optionId)` and
 * calls `setOptionPoints(...)`; neither reads nor writes a block. The block
 * carries no points field at all — `packages/schema`'s `blockSchema` has none,
 * which is what makes "one owner" a fact rather than a convention, and
 * `packages/app/test/editor-rubric.test.ts` asserts a scored block still has no
 * scoring on it.
 *
 * WHY IT MATTERS BEYOND TIDINESS: a criterion is what the calibration bench
 * samples (Story 7.2), what the evidence rows explain (Story 8.4), and what
 * `scores` stores a row per (Story 5.5). Points living on a block would be
 * invisible to all three — the lead would carry a number nothing could account
 * for, which is the opposite of "Humans see why."
 *
 * Story 6.3.
 */
import {
  DEFAULT_CRITERION_WEIGHT,
  applyOperation,
  clipLabel,
  type Block,
  type Condition,
  type Criterion,
  type Funnel,
} from '@leadgible/schema'

/** The criterion that scores a block, if one exists. */
export function criterionFor(funnel: Funnel, blockId: string): Criterion | undefined {
  return funnel.rubric.criteria.find(
    (criterion) => criterion.source === 'answer_ref' && criterion.block_id === blockId,
  )
}

/**
 * What one option is worth, or undefined when the block is not scored at all.
 *
 * Undefined and zero are different answers and the inspector renders them
 * differently: zero is "this option is worth nothing", and undefined is "this
 * question does not feed the score". An operator who cannot tell those apart
 * cannot tell a deliberate zero from a rubric they forgot to write.
 */
export function pointsFor(funnel: Funnel, blockId: string, optionId: string): number | undefined {
  const criterion = criterionFor(funnel, blockId)
  if (!criterion || criterion.points.kind !== 'options') return undefined
  return criterion.points.points_by_option[optionId] ?? criterion.points.default_points
}

/** The criterion's weight — the mockup's "Score out of 25". */
export function weightFor(funnel: Funnel, blockId: string): number | undefined {
  return criterionFor(funnel, blockId)?.weight
}

/**
 * The default a criterion starts at when the inspector first scores a block — the
 * vocabulary's own since Story 27.2, so the builder and a proposal start at one number.
 */
export const DEFAULT_WEIGHT = DEFAULT_CRITERION_WEIGHT

/**
 * The block types an empty `options` map can score — exactly the three
 * `crossReference` accepts one on: "an options map needs a card_select, dropdown
 * or consent block".
 */
const OPTIONS_SCORED: ReadonlySet<Block['type']> = new Set(['card_select', 'dropdown', 'consent'])

/**
 * The questions "Add a criterion" may offer: one of those three, not yet scored.
 *
 * Story 7.1's door offered EVERY block that asks a question and wrote each one an
 * empty options map, so a contact field, a number or a free-text question was one
 * click from a funnel the next autosave refused — and every curated blueprint asks
 * for a name and an email. `test/editor-add-criterion.test.ts` holds this to the
 * validator's own answer for every question type, so the two cannot drift.
 */
export function scorableQuestions(funnel: Funnel): Block[] {
  const scored = new Set(
    funnel.rubric.criteria.flatMap((criterion) =>
      criterion.source === 'answer_ref' ? [criterion.block_id] : [],
    ),
  )
  return funnel.blocks.filter((block) => OPTIONS_SCORED.has(block.type) && !scored.has(block.id))
}

/**
 * Set one option's points, creating the criterion if this is the first.
 *
 * A RECIPE, meant to be handed to `edit()` — it mutates an Immer draft and
 * returns nothing, so the change is a patch and undo covers it like any other.
 * Nothing here calls the store; that keeps this module testable without one and
 * keeps the mutation on the one stack RULE-018 requires.
 *
 * THE VOCABULARY'S `set_option` since Story 27.2 — the operation a proposal
 * calls, which writes what this wrote: a block gains a criterion only when one
 * of its options is worth something (adding a question does not silently start
 * scoring it), at the default weight or the points if they pass it, and a point
 * above the weight raises the weight rather than writing a funnel the validator
 * refuses on save. The inspector's input yields whole numbers; rounding here
 * keeps a caller that does not from being refused.
 */
export function setOptionPoints(
  draft: Funnel,
  block: Block,
  optionId: string,
  points: number,
): void {
  applyOperation(draft, {
    op: 'set_option',
    block_id: block.id,
    id: optionId,
    points: Math.max(0, Math.round(points)),
  })
}

/** Rename the criterion when the question it scores is rewritten. */
export function syncCriterionLabel(draft: Funnel, blockId: string, question: string): void {
  const criterion = draft.rubric.criteria.find(
    (c) => c.source === 'answer_ref' && c.block_id === blockId,
  )
  if (criterion && question.trim().length > 0) criterion.label = clipLabel(question)
}

/**
 * Forget an option, everywhere it is referred to.
 *
 * A points entry keyed on a deleted option is exactly what `crossReference`
 * refuses — `"blk" has no option "opt" — score an option it offers` — so
 * deleting an option without this writes a funnel that cannot be saved.
 */
export function forgetOption(draft: Funnel, blockId: string, optionId: string): void {
  const criterion = draft.rubric.criteria.find(
    (c) => c.source === 'answer_ref' && c.block_id === blockId,
  )
  if (criterion?.points.kind === 'options') {
    delete criterion.points.points_by_option[optionId]
  }
  draft.rubric.disqualifiers = draft.rubric.disqualifiers.filter(
    (d) => !isOptionDisqualifier(d.when, blockId, optionId),
  )
}

/**
 * Forget a block: its criterion, its disqualifiers, the logic rules that read or
 * target it, the extraction fields it feeds and everything that reads those, and
 * its place in a step.
 *
 * A logic rule whose condition reads a deleted question, or whose effect shows,
 * hides or writes a deleted block, can never do anything again — and
 * `crossReference` refuses it with `no block with id` either way. The Logic tab
 * removes a rule only when asked, so this is the one place that can.
 *
 * An extraction field names its BLOCK, and an extracted criterion or condition
 * names the FIELD — so a free-text question reaches two hops out. Stopping at
 * the first hop leaves `crossReference` refusing the next autosave with `no
 * block with id`, naming a question the operator just watched disappear. The
 * curated B2B blueprint ships exactly that chain: `blk_scope` → `fit` →
 * `crit_fit`.
 */
export function forgetBlock(draft: Funnel, blockId: string): void {
  const lostFields = new Set(
    draft.rubric.extraction.filter((f) => f.block_id === blockId).map((f) => f.field_key),
  )
  const readsLostField = (subject: Condition['subject']): boolean =>
    subject.kind === 'extracted' && lostFields.has(subject.field_key)

  if (lostFields.size > 0) {
    draft.rubric.extraction = draft.rubric.extraction.filter((f) => f.block_id !== blockId)
    draft.logic = draft.logic.filter((rule) => !readsLostField(rule.when.subject))
  }
  draft.rubric.criteria = draft.rubric.criteria.filter(
    (c) =>
      !(c.source === 'answer_ref' && c.block_id === blockId) &&
      !(c.source === 'extracted' && lostFields.has(c.field_key)),
  )
  draft.rubric.disqualifiers = draft.rubric.disqualifiers.filter(
    (d) =>
      !(d.when.subject.kind === 'answer' && d.when.subject.block_id === blockId) &&
      !readsLostField(d.when.subject),
  )
  draft.logic = draft.logic.filter(
    (rule) =>
      !(rule.when.subject.kind === 'answer' && rule.when.subject.block_id === blockId) &&
      !('target_block_id' in rule.then && rule.then.target_block_id === blockId),
  )
  draft.blocks = draft.blocks.filter((b) => b.id !== blockId)
  for (const step of draft.steps) step.blocks = step.blocks.filter((id) => id !== blockId)
}

/* ── disqualifiers ──────────────────────────────────────────────────────── */

type When = { subject: { kind: string; block_id?: string }; operator: string; value?: unknown }

const isOptionDisqualifier = (when: When, blockId: string, optionId: string): boolean =>
  when.subject.kind === 'answer' &&
  when.subject.block_id === blockId &&
  when.operator === 'is' &&
  when.value === optionId

/** Is this option a hard gate? */
export function isDisqualifying(funnel: Funnel, blockId: string, optionId: string): boolean {
  return funnel.rubric.disqualifiers.some((d) => isOptionDisqualifier(d.when, blockId, optionId))
}

/**
 * The Disqualify toggle, as a recipe — and the Qualify tab's "Add a disqualifier",
 * which is the same act from the other side.
 *
 * PRD, quoted on the export beside this control: "Anyone choosing this is sent
 * straight to the guide and never scored." RULE-027 is what that means in the
 * data — "a disqualified lead is still stored, still banded and still routed;
 * the disqualifier short-circuits SCORING, not the lead" — so this writes a
 * `rubric.disqualifiers[]` entry and touches nothing about capture.
 *
 * THE VOCABULARY'S `set_option` since Story 27.2, which names the gate after the
 * question and the option's own label. Idempotent where the Qualify tab's door was
 * not: that door pushed `dq_{block}_{option}` whether or not the inspector had
 * already written it, and two gates sharing an id are a rubric the save refuses.
 * The label changed wording for that door, from "{question} is {option}", and no
 * screen shows it — the tab draws each gate as a sentence from its condition.
 */
export function setDisqualifying(draft: Funnel, block: Block, optionId: string, on: boolean): void {
  applyOperation(draft, { op: 'set_option', block_id: block.id, id: optionId, disqualifies: on })
}
